import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import HomePage from './pages/HomePage';
import usePerformance from './hooks/usePerformance';
import { preloadCriticalRoutes } from './utils/lazyImports';
import GoogleAnalytics from './components/analytics/GoogleAnalytics';
import SearchConsoleVerification from './components/analytics/SearchConsoleVerification';
import { initWebVitals, trackPageLoadPerformance } from './utils/webVitalsGA';
import { SpeedInsights } from '@vercel/speed-insights/react';

// Lazy load non-critical pages
import {
  LazyAboutPage,
  LazyAccreditationPage,
  LazyFacultyPage,
  LazyAcademicsPage,
  LazyCurriculumPage,
  LazyElementarySchoolPage,
  LazyMiddleSchoolPage,
  LazyHighSchoolPage,
  LazyPreschoolPage,
  LazyContactPage,
  LazyAdmissionsPage,
  LazyActivitiesPage,
  LazyCommunityServicePage,
  LazyCalendarPage,
  LazyResourcesPage,
  LazySocialMediaPage,
  LazyLearningSupport,
  LazyUniversityCounselingPage,
  LazyEmploymentPage,
  LazyTechnologyPage,
  LazyAlumniPage,
  LazyAcademicResultsPage,
  LazyNewsPage,
  LazyParentResourcesPage,
  LazyFaqPage,
  LazyGalleryPage,
  LazyHealthSafetyPage,
  LazyEmergencyProtocolsPage,
  LazyEarlyYearsPage
} from './utils/lazyImports';
import useScrollTop from './hooks/useScrollTop';
import i18n from './i18n/i18n';

// Component that uses the scroll hook
const ScrollToTopWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useScrollTop();
  return <>{children}</>;
};

// Language redirect component to handle default route
const LanguageRedirect: React.FC = () => {
  const preferredLanguage = i18n.language.split('-')[0]; // Get the language code without region
  const validLanguages = ['en', 'fr', 'ar'];
  
  // If the current language is supported, redirect to it, otherwise default to English
  const targetLanguage = validLanguages.includes(preferredLanguage) ? preferredLanguage : 'en';
  
  return <Navigate to={`/${targetLanguage}`} replace />;
};

// Loading component
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tlc-navy mx-auto mb-4"></div>
      <p className="text-tlc-navy font-medium">Loading...</p>
    </div>
  </div>
);

// Component to set up routes for a specific language
const LanguageRoutes: React.FC<{ lang: string }> = ({ lang }) => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<LazyAboutPage />} />
        <Route path="/accreditation" element={<LazyAccreditationPage />} />
        <Route path="/faculty" element={<LazyFacultyPage />} />
        <Route path="/academics" element={<LazyAcademicsPage />} />
        <Route path="/curriculum" element={<LazyCurriculumPage />} />
        <Route path="/preschool" element={<LazyPreschoolPage />} />
        <Route path="/elementary-school" element={<LazyElementarySchoolPage />} />
        <Route path="/middle-school" element={<LazyMiddleSchoolPage />} />
        <Route path="/high-school" element={<LazyHighSchoolPage />} />
        <Route path="/contact" element={<LazyContactPage />} />
        <Route path="/admissions" element={<LazyAdmissionsPage />} />
        <Route path="/activities" element={<LazyActivitiesPage />} />
        <Route path="/community-service" element={<LazyCommunityServicePage />} />
        <Route path="/calendar" element={<LazyCalendarPage />} />
        <Route path="/resources" element={<LazyResourcesPage />} />
        <Route path="/social-media" element={<LazySocialMediaPage />} />
        <Route path="/learning-support" element={<LazyLearningSupport />} />
        <Route path="/university-counseling" element={<LazyUniversityCounselingPage />} />
        <Route path="/employment" element={<LazyEmploymentPage />} />
        <Route path="/technology" element={<LazyTechnologyPage />} />
        <Route path="/alumni" element={<LazyAlumniPage />} />
        <Route path="/academic-results" element={<LazyAcademicResultsPage />} />
        <Route path="/news" element={<LazyNewsPage />} />
        <Route path="/parent-resources" element={<LazyParentResourcesPage />} />
        <Route path="/faq" element={<LazyFaqPage />} />
        <Route path="/gallery" element={<LazyGalleryPage />} />
        <Route path="/health-safety" element={<LazyHealthSafetyPage />} />
        <Route path="/emergency-protocols" element={<LazyEmergencyProtocolsPage />} />
        <Route path="/early-years" element={<LazyEarlyYearsPage />} />
      </Routes>
    </Suspense>
  );
};

// Language route listener component
const LanguageRouteListener: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentPath = window.location.pathname;
  const pathSegments = currentPath.split('/').filter(Boolean);
  
  // Check if the first segment is a language code
  if (pathSegments.length > 0) {
    const potentialLang = pathSegments[0];
    const validLanguages = ['en', 'fr', 'ar'];
    
    if (validLanguages.includes(potentialLang) && i18n.language !== potentialLang) {
      // Set the language based on URL if it's different from current
      i18n.changeLanguage(potentialLang);
    }
  }
  
  return <>{children}</>;
};

function App() {
  // Enable performance monitoring
  usePerformance();

  // Initialize analytics and performance tracking
  useEffect(() => {
    // Preload critical routes
    preloadCriticalRoutes();
    
    // Initialize Web Vitals with Google Analytics integration
    initWebVitals();
    
    // Track page load performance
    trackPageLoadPerformance();
  }, []);

  return (
    <Router>
      <LanguageRouteListener>
        <ScrollToTopWrapper>
          {/* Google Analytics and Search Console Integration */}
          <GoogleAnalytics />
          <SearchConsoleVerification />
          
          {/* Vercel Speed Insights */}
          <SpeedInsights />
          
          <Routes>
            {/* Root route - redirect to preferred language */}
            <Route path="/" element={<LanguageRedirect />} />
            
            {/* Language-specific routes */}
            <Route path="/en/*" element={<LanguageRoutes lang="en" />} />
            <Route path="/fr/*" element={<LanguageRoutes lang="fr" />} />
            <Route path="/ar/*" element={<LanguageRoutes lang="ar" />} />
            
            {/* Fallback for any other path */}
            <Route path="*" element={<LanguageRedirect />} />
        </Routes>
        </ScrollToTopWrapper>
      </LanguageRouteListener>
      </Router>
  );
}

export default App;
